/**
 * Invoice frequency — presets and custom intervals (days / weeks / months)
 * Presets: monthly, quarterly, halfYearly, yearly, oneTime
 * Custom:  every:N:days | every:N:weeks | every:N:months
 */

export const PRESET_FREQUENCIES = ['monthly', 'quarterly', 'halfYearly', 'yearly', 'oneTime'];

const PRESET_LABELS = {
    monthly: 'Monthly (every 1 month)',
    quarterly: 'Quarterly (every 3 months)',
    halfYearly: 'Half-Yearly (every 6 months)',
    yearly: 'Yearly (every 12 months)',
    oneTime: 'One-Time'
};

const PRESET_SUFFIX = {
    monthly: '/mo',
    quarterly: '/qtr',
    halfYearly: '/6mo',
    yearly: '/yr',
    oneTime: ' once'
};

/**
 * @returns {{ type: 'oneTime'|'preset'|'custom', preset?: string, interval: number, unit: 'days'|'weeks'|'months' }}
 */
export function parseFrequency(value) {
    if (!value || value === 'monthly') {
        return { type: 'preset', preset: 'monthly', interval: 1, unit: 'months' };
    }
    if (value === 'oneTime') {
        return { type: 'oneTime', preset: 'oneTime', interval: 0, unit: 'months' };
    }
    if (PRESET_FREQUENCIES.includes(value)) {
        const intervals = {
            quarterly: 3,
            halfYearly: 6,
            yearly: 12
        };
        return {
            type: 'preset',
            preset: value,
            interval: intervals[value] || 1,
            unit: 'months'
        };
    }
    const match = String(value).match(/^every:(\d+):(days|weeks|months)$/);
    if (match) {
        return {
            type: 'custom',
            preset: null,
            interval: Math.max(1, parseInt(match[1], 10)),
            unit: match[2]
        };
    }
    return { type: 'preset', preset: 'monthly', interval: 1, unit: 'months' };
}

export function buildFrequencyValue(preset, interval, unit) {
    if (preset === 'custom') {
        const n = Math.max(1, parseInt(interval, 10) || 1);
        return `every:${n}:${unit || 'months'}`;
    }
    return preset;
}

export function getFrequencyLabel(frequency) {
    const parsed = parseFrequency(frequency);
    if (parsed.type === 'oneTime') return 'One-Time';
    if (parsed.type === 'preset') return PRESET_LABELS[parsed.preset] || parsed.preset;
    const unitLabel = parsed.interval === 1
        ? { days: 'day', weeks: 'week', months: 'month' }[parsed.unit]
        : { days: 'days', weeks: 'weeks', months: 'months' }[parsed.unit];
    return `Every ${parsed.interval} ${unitLabel}`;
}

export function getAmountSuffix(frequency) {
    const parsed = parseFrequency(frequency);
    if (parsed.type === 'oneTime') return PRESET_SUFFIX.oneTime;
    if (parsed.type === 'preset') return PRESET_SUFFIX[parsed.preset] || '';
    if (parsed.unit === 'days') return parsed.interval === 1 ? '/day' : `/${parsed.interval}d`;
    if (parsed.unit === 'weeks') return parsed.interval === 1 ? '/wk' : `/${parsed.interval}wks`;
    if (parsed.unit === 'months') return parsed.interval === 1 ? '/mo' : `/${parsed.interval}mo`;
    return '';
}

export function getNextInvoiceDate(currentDate, frequency) {
    const parsed = parseFrequency(frequency);
    if (parsed.type === 'oneTime') return new Date(9999, 0, 1);

    const next = new Date(currentDate);
    const n = parsed.interval;

    switch (parsed.unit) {
        case 'days':
            next.setDate(next.getDate() + n);
            break;
        case 'weeks':
            next.setDate(next.getDate() + n * 7);
            break;
        case 'months':
            next.setMonth(next.getMonth() + n);
            break;
        default:
            next.setMonth(next.getMonth() + 1);
    }

    return next;
}

export function toDateString(date) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}

export function getNextInvoiceDateString(dateStr, frequency) {
    const parsed = parseFrequency(frequency);
    if (parsed.type === 'oneTime') return '9999-12-31';

    const parts = dateStr.split('-').map(Number);
    const current = new Date(parts[0], parts[1] - 1, parts[2]);
    const next = getNextInvoiceDate(current, frequency);
    const nextStr = toDateString(next);

    if (nextStr <= dateStr) return '9999-12-31';
    return nextStr;
}

export function countInvoices(firstDateStr, lastDateStr, frequency) {
    const first = new Date(firstDateStr + 'T00:00:00');
    const last = new Date(lastDateStr + 'T00:00:00');
    let count = 0;
    let current = new Date(first);

    while (current <= last) {
        count++;
        const next = getNextInvoiceDate(current, frequency);
        if (next.getFullYear() >= 9999) break;
        current = next;
    }

    return count;
}

/**
 * Populate frequency form controls from stored value
 */
export function applyFrequencyToForm(frequency) {
    const presetEl = document.getElementById('invoiceFrequencyPreset');
    const typePreset = document.getElementById('invoiceFrequencyTypePreset');
    const typeCustom = document.getElementById('invoiceFrequencyTypeCustom');
    const intervalEl = document.getElementById('invoiceFrequencyInterval');
    const unitEl = document.getElementById('invoiceFrequencyUnit');
    const hiddenEl = document.getElementById('invoiceFrequency');
    if (!presetEl || !hiddenEl) return;

    const parsed = parseFrequency(frequency);
    const isCustom = parsed.type === 'custom';

    if (typePreset) typePreset.checked = !isCustom;
    if (typeCustom) typeCustom.checked = isCustom;

    if (isCustom) {
        if (intervalEl) intervalEl.value = String(parsed.interval);
        if (unitEl) unitEl.value = parsed.unit;
    } else {
        presetEl.value = parsed.preset || 'monthly';
    }

    syncFrequencyFormUI();
    hiddenEl.value = frequency || readFrequencyFromForm();
}

/**
 * Toggle panels, enable/disable inputs, update preview & hidden value
 */
export function syncFrequencyFormUI() {
    const presetPanel = document.getElementById('invoiceFrequencyPresetPanel');
    const customPanel = document.getElementById('invoiceFrequencyCustomPanel');
    const typeCustom = document.getElementById('invoiceFrequencyTypeCustom');
    const presetEl = document.getElementById('invoiceFrequencyPreset');
    const intervalEl = document.getElementById('invoiceFrequencyInterval');
    const unitEl = document.getElementById('invoiceFrequencyUnit');
    const previewEl = document.getElementById('invoiceFrequencyPreview');
    const hiddenEl = document.getElementById('invoiceFrequency');

    const isCustom = typeCustom?.checked === true;

    if (presetPanel) presetPanel.toggleAttribute('hidden', isCustom);
    if (customPanel) customPanel.toggleAttribute('hidden', !isCustom);

    if (presetEl) presetEl.disabled = isCustom;
    if (intervalEl) intervalEl.disabled = !isCustom;
    if (unitEl) unitEl.disabled = !isCustom;

    const value = readFrequencyFromForm();
    if (hiddenEl) hiddenEl.value = value;

    if (previewEl) {
        const label = getFrequencyLabel(value);
        previewEl.textContent = value === 'oneTime'
            ? 'Single invoice — no recurring schedule'
            : `Bills ${label.toLowerCase()}`;
    }
}

/**
 * Read combined frequency value from form controls
 */
export function readFrequencyFromForm() {
    const typeCustom = document.getElementById('invoiceFrequencyTypeCustom');
    const presetEl = document.getElementById('invoiceFrequencyPreset');
    const intervalEl = document.getElementById('invoiceFrequencyInterval');
    const unitEl = document.getElementById('invoiceFrequencyUnit');

    if (typeCustom?.checked) {
        return buildFrequencyValue('custom', intervalEl?.value, unitEl?.value || 'months');
    }
    return presetEl?.value || 'monthly';
}
